import { useCallback } from 'react';

import { ShouldNotHappen } from 'utils';

import {
  ConditionsReactivateEtatDto,
  EtatDto,
  EtatDtoEtatEnum,
  EtatRequestDto,
  EtatRequestDtoEtatEnum,
  ReferentielSearchResultDtoEtatDto,
  ReferentielUsesDto,
} from 'lib_api/lib/api/gen';

import {
  FetchDataResult,
  TablePagination,
  TableSort,
} from 'components/WrappedComponents/Table/types';
import { useApi } from 'hooks/ApiStoreContext';
import { FilterDashboardEtat } from 'types/referentiels/Etat';

import { EtatFormValue, EtatSort, useChangeValidityEtatReturn } from './types';

export function useSearchEtat(
  filter: FilterDashboardEtat,
  sort: TableSort<EtatSort>,
  pagination: TablePagination,
): [
  () => Promise<ReferentielSearchResultDtoEtatDto>,
  (result: ReferentielSearchResultDtoEtatDto) => FetchDataResult<EtatDto>,
] {
  const controller = useApi().AdminFonctionnelControllerApi;

  return [
    () => {
      return controller.searchEtatUsingSpecsUsingGET({
        filters: {
          ...filter,
          ...pagination,
        },
        sorts: {
          etat: sort?.ETAT,
        },
      });
    },
    result => {
      return {
        data: result.results.referenceDtoList,
        total: result.total,
      };
    },
  ];
}

export function buildDto(values: EtatFormValue): EtatRequestDto {
  return {
    etat: values.etat ?? null,
    detail: values.detail ?? null,
    libelleCourt: values.libelleCourt ?? null,
  };
}

export function buildDefaultUpdateFormValues(etat: EtatDto): EtatFormValue {
  return {
    etat: mapEtatEnum(etat.etat),
    detail: etat.detail ?? undefined,
    libelleCourt: etat.libelleCourt ?? undefined,
  };
}

function mapEtatEnum(etatEnum: EtatDtoEtatEnum): EtatRequestDtoEtatEnum {
  switch (etatEnum) {
    case EtatDtoEtatEnum.BON_ETAT:
      return EtatRequestDtoEtatEnum.BON_ETAT;
    case EtatDtoEtatEnum.DEGRADE:
      return EtatRequestDtoEtatEnum.DEGRADE;
    case EtatDtoEtatEnum.DOMMAGES_GRAVES:
      return EtatRequestDtoEtatEnum.DOMMAGES_GRAVES;
    default:
      throw new ShouldNotHappen(etatEnum);
  }
}

export function useChangeValidityEtat(): useChangeValidityEtatReturn {
  const controller = useApi().AdminFonctionnelControllerApi;

  const submitDeprecateEtat = useCallback(
    (idCorrelation: string): Promise<Response> => {
      return controller.deprecateEtatUsingPUT(idCorrelation);
    },
    [controller],
  );

  const submitReactivateEtat = useCallback(
    (idCorrelation: string): Promise<Response> => {
      return controller.reactivateEtatUsingPUT(idCorrelation);
    },
    [controller],
  );

  const conditionsReactivateEtat = useCallback(
    (idCorrelation: string): Promise<ConditionsReactivateEtatDto> => {
      return controller.conditionReactivationEtatUsingGET(idCorrelation);
    },
    [controller],
  );

  const getUsesEtat = useCallback(
    (idCorrelation: string): Promise<ReferentielUsesDto> => {
      return controller.searchUsesEtatUsingGET(idCorrelation);
    },
    [controller],
  );

  return {
    submitReactivateEtat,
    submitDeprecateEtat,
    conditionsReactivateEtat,
    getUsesEtat,
  };
}
